/** @jsx jsx */
import * as React from "react";
import { css, jsx } from "@emotion/core"
import { isEmpty as _isEmpty } from "lodash";
import { IAppState } from "../models/IAppState";
import { connect, Dispatch } from "react-redux";
import IDoctorProfile from "../models/IDoctorProfile";
import { IDefaultWorkingHoursRequest, IUpdateDefaultWorkingHoursRequest } from "../models/defaultWorkingHours/IDefaultWorkingHoursRequest";
import { DefaultWorkingHourActions } from "../actions/DefaultWorkingHoursActions";
import { IDefaultWorkingHoursResponse } from "../models/defaultWorkingHours/IDefaultWorkingHoursResponse";
import { DefaultWorkingHoursStyles } from "../styles/components/defaultWorkingHours/defaultWorkingHours";
import { ROSTER } from "../routes";
import { Link } from "react-router";
import AppUtils from "../utils/AppUtils";
import { RosterCalendarStyles, rosterInnerContainer } from "../styles/components/Roster/RosterCalendar";
import { CALENDAR_DAYS, DEFAULT_WORKING_HOURS_ACTIONS } from "../utils/AppConstants";
import moment = require("moment");
import { ITimeInterval } from "../models/Roster/ITimeInterval";
import { ShiftCard } from "../components/defaultWorkingHours/ShiftCard";
import AppLoader from "../components/utils/AppLoader";
import LocalStorageUtils from "../utils/LocalStorageUtils";
import { EventNames } from "../utils/EventTrackerConstants";
import { Tracker } from "../utils/Tracker";
import { EditRosterModal } from "../components/Roster/EditRosterModa";
import { AddRosterModal } from "../components/Roster/AddRosterModal";
import { CopyDefaultWorkingHoursModal } from "../components/defaultWorkingHours/CopyDefaultWorkingHours";
import { rosterStyles } from "../styles/roster/mobile/roster";
import { AlertModal } from "../components/Roster/AlertModal";
import { WarningInline } from "../components/common/WarningInline";

interface IDefaultWorkingHoursProps {
    selectedDoctor: IDoctorProfile;
    fetchDefaultWorkingHours: (params: IDefaultWorkingHoursRequest) => Promise<IDefaultWorkingHoursResponse[]>;
    updateDefaultWorkingHours: (params: IUpdateDefaultWorkingHoursRequest[], doctorId: number) => Promise<IDefaultWorkingHoursResponse[]>;
}

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & IDefaultWorkingHoursProps
const DefaultWorkingHoursContainer: React.FC<Props> = (props: IDefaultWorkingHoursProps) => {
    const { selectedDoctor } = props;

    const [defaultHours, setDefaultHours] = React.useState<IDefaultWorkingHoursResponse[]>(null)
    const [groupedDayWise, setGroupedDayWiseHours] = React.useState(null);
    const [isLoading, setLoader] = React.useState<boolean>(false);
    const [isEditModalOpen, setEditModalDisplay] = React.useState<boolean>(false);
    const [exisitngShiftsToEdit, setExistingShiftsToEdit] = React.useState(null);
    const [dayCurrentlyAdding, setDayCurrentlyAdding] = React.useState<string>(null);
    const [expandedCardIndex, setExpandedCardIndex] = React.useState<number>(-1);
    const [isCopyShiftModalOpen, setCopyShiftModalDisplay] = React.useState<boolean>(false);
    const [shiftToCopy, setShiftToCopy] = React.useState(null);
    const [datesAvailableToCopyTo, setDatesAvailableToCopyTo] = React.useState([]);
    const [errorMsg, setErrorMsg] = React.useState(null);
    const [isAddRosterModalOpen, setAddModalDisplay] = React.useState<boolean>(false);
    const [dateCurrentlyAdding, setDateCurrentlyAdding] = React.useState(null);

    const isMobile: boolean = AppUtils.getIsMobile();
    const agentCentreMissing = _isEmpty(selectedDoctor.agentCenterMapping);
    const agentMappedWithMultipleCentres = !agentCentreMissing && selectedDoctor.agentCenterMapping.length > 1;

    React.useEffect(() => {
        // On Mount
        Tracker.pageOpened(EventNames.Automated_Working_Hours);
        if (!agentCentreMissing && !agentMappedWithMultipleCentres) {
            setLoader(true);
            getDoctorDefaultWorkingHours();
        }
    }, []);

    const getDoctorDefaultWorkingHours = () => {
        const { selectedDoctor } = props;
        const params: IDefaultWorkingHoursRequest = { doctorId: selectedDoctor.id, centerId: selectedDoctor.agentCenterMapping[0] && selectedDoctor.agentCenterMapping[0].centerId }
        props.fetchDefaultWorkingHours(params).then(response => {
            setLoader(false);
            setDefaultHours(response);
            let dayWiseDates: any = { [CALENDAR_DAYS.Monday.value]: [], [CALENDAR_DAYS.Tuesday.value]: [], [CALENDAR_DAYS.Wednesday.value]: [], [CALENDAR_DAYS.Thursday.value]: [], [CALENDAR_DAYS.Friday.value]: [], [CALENDAR_DAYS.Saturday.value]: [], [CALENDAR_DAYS.Sunday.value]: [] }
            response.forEach(rosterDate => {
                dayWiseDates[rosterDate.dayOfWeek].push(rosterDate);
            })
            setGroupedDayWiseHours(dayWiseDates);
        })
    }

    const openEditModal = (day: string): void => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Edit_Automated_Working_Hours, widget: EventNames.Day_Widget, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
        setEditModalDisplay(true);
        setExistingShiftsToEdit(groupedDayWise[day]);
        setDayCurrentlyAdding(day);
    }

    const closeEditModal = (): void => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Close, widget: EventNames.Edit_Automated_Working_Hours, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
        Tracker.track(EventNames.Edit_Automated_Working_Hours_Closed);
        setEditModalDisplay(false);
        setExistingShiftsToEdit(null);
        setDayCurrentlyAdding(null);
    }

    const openAddHoursModal = (day: string) => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Add_Automated_Working_Hours, widget: EventNames.Day_Widget, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
        setAddModalDisplay(true);
        setDayCurrentlyAdding(day);
    }

    const closeAddModal = (): void => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Close, widget: EventNames.Add_Automated_Working_Hours, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
        Tracker.track(EventNames.Add_Automated_Working_Hours_Closed);
        setAddModalDisplay(false);
        setDateCurrentlyAdding(null);
    }

    const openCopyModal = (shiftToCopy: ITimeInterval, day: string, isMobile?: boolean): void => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Copy_Automated_Working_Hours, widget: EventNames.Day_Widget, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours })
        const dayList: string[] = groupedDayWise ? Object.keys(groupedDayWise) : [];
        if (isMobile) {
            const selectedDayIndex = Object.keys(CALENDAR_DAYS).findIndex(key => CALENDAR_DAYS[key].value === day);
            dayList.splice(selectedDayIndex, 1);
        }
        setDatesAvailableToCopyTo(dayList);
        setDateCurrentlyAdding(day);
        setShiftToCopy(shiftToCopy);
        setCopyShiftModalDisplay(true);
    }

    const closeCopyModal = (): void => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Close, widget: EventNames.Copy_Automated_Working_Hours, flow: EventNames.Automated_Working_Hours, section: EventNames.Automated_Working_Hours });
        Tracker.track(EventNames.Copy_Automated_Working_Hours_Closed);
        setCopyShiftModalDisplay(false);
        setShiftToCopy(null);
        setDatesAvailableToCopyTo([]);
    }

    const updateRoster = (addParams: IUpdateDefaultWorkingHoursRequest[], userAction?: DEFAULT_WORKING_HOURS_ACTIONS): void => {
        props.updateDefaultWorkingHours(addParams, props.selectedDoctor.id).then(res => {
            switch (userAction) {
                case DEFAULT_WORKING_HOURS_ACTIONS.ADD: closeAddModal();
                    break;
                case DEFAULT_WORKING_HOURS_ACTIONS.EDIT: closeEditModal();
                    break;
                case DEFAULT_WORKING_HOURS_ACTIONS.COPY: closeCopyModal();
                    break;
            }
            getDoctorDefaultWorkingHours();
        }).catch(err => {
            setErrorMsg(`Failed to update working hours:  ${err}`);
        });
    }

    const applyExpandedCardIndex = (index: number): void => {
        if (index === expandedCardIndex) {
            setExpandedCardIndex(-1);
        } else {
            setExpandedCardIndex(index);
        }
    }

    const closeErrorModal = (): void => {
        setErrorMsg(null);
    }

    return (
        <div css={!isMobile && DefaultWorkingHoursStyles.outerContainer}>
            <div css={DefaultWorkingHoursStyles.backNav}>
                <header css={isMobile ? DefaultWorkingHoursStyles.mobilePageHeader : DefaultWorkingHoursStyles.header}>
                    <Link to={ROSTER}> <img css={DefaultWorkingHoursStyles.backArrow} src="/images/back-arrow.png"></img></Link>
                    <div css={DefaultWorkingHoursStyles.headerTitle}>Back to Working Hours</div>
                </header>
            </div>

            <div css={DefaultWorkingHoursStyles.subHeader}>
                <div css={DefaultWorkingHoursStyles.headerTitle}>Automated Working Hours</div>
                <WarningInline errMsg={`Any changes that you make will take effect from ${AppUtils.getDateFromNumberOfDays()}`} />
            </div>
            {agentCentreMissing &&
                <div className="warning">
                    <div>* Cannot create your automated working hours.</div>
                    <div>You are currently not assigned to any center. Please contact Care.Fit team.</div>
                </div>
            }
            {agentMappedWithMultipleCentres &&
                <div className="warning">
                    <div>* Cannot create your automated working hours.</div>
                    <div>You are currently assigned to multiple centers. Please contact Care.Fit team.</div>
                </div>
            }

            {isLoading && <AppLoader />}
            {
                !isMobile &&
                <div css={RosterCalendarStyles.rosterWrapper}>
                    <div css={RosterCalendarStyles.rosterOuterContainer}>
                        {groupedDayWise && Object.keys(groupedDayWise).map((day: string, index: number) => {
                            return (
                                <div className={rosterInnerContainer} key={`${day}-${index}`}>
                                    <div css={RosterCalendarStyles.cardHeader}>
                                        <div css={RosterCalendarStyles.calendarDayLabel}>{Object.keys(CALENDAR_DAYS).find(key => CALENDAR_DAYS[key].value === day)}</div>
                                        <div css={RosterCalendarStyles.editWrapper} onClick={() => openEditModal(day)} >
                                            {groupedDayWise[day].length > 0 &&
                                                <React.Fragment>
                                                    <img css={RosterCalendarStyles.editImg} src="/images/edit-pencil.svg" />
                                                    <span css={RosterCalendarStyles.editLabel}>EDIT</span>
                                                </React.Fragment>
                                            }
                                        </div>
                                    </div>
                                    <ShiftCard slotsForTheDay={groupedDayWise[day]} date={day} key={day}
                                        openCopyModal={(interval: ITimeInterval) => openCopyModal(interval, day)}
                                        openAddHoursModal={() => openAddHoursModal(day)}
                                        onCardExpand={() => applyExpandedCardIndex(index)}
                                        style={expandedCardIndex !== index && expandedCardIndex !== -1 ? { opacity: 0.2 } : {}}
                                    />
                                </div>
                            )
                        })
                        }
                    </div>
                </div>
            }
            <div css={isMobile ? rosterStyles.rosterModalMobileTimeSelectors : {}} className="roster-container">
                {
                    isMobile && groupedDayWise && Object.keys(groupedDayWise).map((date: string, index: number) => {

                        return (
                            <ShiftCard slotsForTheDay={groupedDayWise[date]} date={CALENDAR_DAYS[Object.keys(CALENDAR_DAYS).find(key => CALENDAR_DAYS[key].value === date)].shortForm} key={date} openEditModal={() => openEditModal(date)}
                                openCopyModal={(interval: ITimeInterval) => openCopyModal(interval, date, true)} onCardExpand={() => setExpandedCardIndex(index)}
                                openAddHoursModal={openAddHoursModal}
                                style={expandedCardIndex !== index && expandedCardIndex !== -1 ? { opacity: 0.2 } : {}} />
                        )
                    })
                }
            </div>
            <div css={isMobile ? DefaultWorkingHoursStyles.workingHoursDescriptionWrapperMobile : DefaultWorkingHoursStyles.workingHoursDescriptionWrapper}>
                <img css={isMobile && DefaultWorkingHoursStyles.defaultHoursImage} src="/images/base-working-hours.svg"></img>
                <div css={isMobile ? DefaultWorkingHoursStyles.mobileDescription : DefaultWorkingHoursStyles.description}>
                    <header css={DefaultWorkingHoursStyles.heading}> What are Automated working hours?</header>
                    <ul css={DefaultWorkingHoursStyles.listWrapper}>
                        <li css={DefaultWorkingHoursStyles.listItems}><span css={DefaultWorkingHoursStyles.listLabel}>These are the default working hours for which we will accept appointment bookings for you.</span></li>
                        <li css={DefaultWorkingHoursStyles.listItems}><span css={DefaultWorkingHoursStyles.listLabel}>Appointment booking slots will automatically open up 14 days in advance based on the default working hours.</span> </li>
                        <li css={DefaultWorkingHoursStyles.listItems}><span css={DefaultWorkingHoursStyles.listLabel}>If there is a change in your schedule, you can update your working hours from the working hours tab on Care.fit EMR.</span></li>
                    </ul>
                </div>
            </div>
            {
                isEditModalOpen &&
                <EditRosterModal toggleForceCloseModal={closeEditModal} title="Edit Automated Working Hours" updateRoster={updateRoster}
                    date={dayCurrentlyAdding} selectedDoctor={selectedDoctor} exisitngShifts={exisitngShiftsToEdit} isDefaultWorkingHoursAction={true} />
            }
            {
                isAddRosterModalOpen &&
                <AddRosterModal toggleForceCloseModal={closeAddModal} title="Add Automated Working Hours"
                    date={dayCurrentlyAdding} addDefaultWorkingHoursShift={updateRoster} selectedDoctor={selectedDoctor} isDefaultWorkingHoursAction={true} />
            }
            {
                isCopyShiftModalOpen &&
                <CopyDefaultWorkingHoursModal shift={shiftToCopy} datesAvailableToCopy={datesAvailableToCopyTo} selectedDoctor={selectedDoctor}
                    closeCopyModal={closeCopyModal} copyShifts={updateRoster}
                    allDaysData={groupedDayWise} title="Copy Automated Working Hours" dateCurrentlyCopying={dateCurrentlyAdding} />
            }
            {
                errorMsg &&
                <AlertModal header={<div>Failed</div>} body={<div>{errorMsg}</div>} cancelBtnProps={{ clickHandler: closeErrorModal, text: "Cancel" }}></AlertModal>
            }
        </div>
    )
}

const mapStateToProps = (state: IAppState) => {
    return {
        selectedDoctor: state.doctorProfile
    };
}


const mapDispatchToProps = (dispatch: Dispatch<any>) => {
    return {
        fetchDefaultWorkingHours: (params: IDefaultWorkingHoursRequest) => DefaultWorkingHourActions.fetchDoctorDefaultWorkingHours(params),
        updateDefaultWorkingHours: (addParams: IUpdateDefaultWorkingHoursRequest[], doctorId: number) => DefaultWorkingHourActions.updateDoctorDefaultWorkingHours(addParams, doctorId)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(DefaultWorkingHoursContainer);